<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBRL } from 'dashboard/helper/lais/currency';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  loading: { type: Boolean, default: false },
  summary: { type: Array, default: () => [] },
});

const emit = defineEmits(['viewProduction']);
const { t } = useI18n();
const productionStatusKeys = [
  'em_andamento',
  'liberado',
  'solicitado',
  'em_transferencia',
  'concluido',
];
const total = computed(
  () => props.summary.find(item => item.key === 'total') || { count: 0 }
);
const statuses = computed(() =>
  productionStatusKeys.map(
    key =>
      props.summary.find(item => item.key === key) || {
        key,
        count: 0,
        valor: { amount: 0 },
      }
  )
);
const statusLabel = key =>
  ({
    em_andamento: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.EM_ANDAMENTO'),
    liberado: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.LIBERADO'),
    solicitado: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.SOLICITADO'),
    em_transferencia: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.EM_TRANSFERENCIA'),
    concluido: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.CONCLUIDO'),
  })[key];
const statusClass = key =>
  ({
    em_andamento: 'bg-yellow-100 text-yellow-800',
    liberado: 'bg-green-100 text-green-800',
    solicitado: 'bg-teal-100 text-teal-800',
    em_transferencia: 'bg-emerald-100 text-emerald-800',
    concluido: 'bg-green-100 text-green-800',
  })[key];
const money = value =>
  formatBRL(value?.amount, {
    fallback: 'R$ 0,00',
    fractionDigits: 2,
  });
</script>

<template>
  <section
    data-testid="broker-production-summary"
    class="grid gap-3 xl:grid-cols-[minmax(280px,0.9fr)_minmax(0,2.1fr)]"
    :aria-busy="loading"
  >
    <LaisCard density="tight">
      <div class="flex min-h-40 flex-col justify-between gap-4 p-4">
        <p class="text-sm font-semibold text-lais-text">
          {{ t('REAL_ESTATE_BROKER_DASHBOARD.PRODUCTION.TOTAL') }}
        </p>
        <div class="border-t border-lais-border-soft pt-4">
          <p class="text-2xl font-bold text-lais-text">
            {{
              loading
                ? t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.LOADING')
                : money(total.valor)
            }}
          </p>
        </div>
        <LaisPrimaryButton
          v-if="total.count > 0"
          data-testid="broker-production-summary-view"
          size="sm"
          variant="link"
          class="self-start"
          @click="emit('viewProduction', total.count)"
        >
          {{
            t('REAL_ESTATE_BROKER_DASHBOARD.PRODUCTION.VIEW', {
              count: total.count,
            })
          }}
        </LaisPrimaryButton>
      </div>
    </LaisCard>
    <div
      class="grid overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card sm:grid-cols-2 xl:grid-cols-5"
    >
      <article
        v-for="item in statuses"
        :key="item.key"
        :data-testid="`broker-production-status-${item.key}`"
        class="flex min-h-40 flex-col justify-between gap-4 border-b border-lais-border-soft p-4 last:border-b-0 sm:border-r xl:border-b-0"
      >
        <span
          class="w-fit rounded-full px-2 py-1 text-xs font-semibold"
          :class="statusClass(item.key)"
        >
          {{ statusLabel(item.key) }}
        </span>
        <p class="text-lg font-bold text-lais-text">
          {{
            loading
              ? t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.LOADING')
              : money(item.valor)
          }}
        </p>
      </article>
    </div>
  </section>
</template>
